import React, { memo } from "react"
import { makeStyles } from "@material-ui/styles"
import { NavLink } from "react-router-dom"
import { Box, Hidden } from "@material-ui/core"
import { ActionCard } from "./ActionCard"
import { LinearProgress, Assignees, AdornmentIcon } from ".."
import { useDateUtils, useJobUtils } from "../../utils"
import { AssigneeAvatars } from "../Assignees"
import { FormatHumanTime } from "../Format"
import { AuthorTools } from "../Author"
import { ActionCardInfo } from "./ActionCardInfo"
import { PROCESS_REPEAT, TASK_RESPONSE_TYPE } from "../../data"
import { RowBox } from "../Boxes"
import TimeAgo from "../TimeAgo"
import { useAuth } from "../../services"
import { SingleStepProcessActionCard } from "./SingleStepProcessActionCard"

const mrInfo = 3

const useStyles = makeStyles((theme) => ({
  overdue: {},
  overdueIcon: {
    color: "#fff",
    backgroundColor: theme.palette.warning.main,
  },
  info: {
    fontSize: 14,
    color: theme.palette.text.light,
    marginBottom: theme.spacing(1),
  },
  assigneesRight: {
    textAlign: "right",
  },
  rightChildren: {
    height: 40,
    overflow: "visible",
  },
  progress: {
    width: 140,
  },
  progressXs: {},
}))

const ProcessStepsActionCardInfo = memo(function ProcessStepsActionCardInfo({ count, ...rest }) {
  const suffix = count > 1 ? "steps" : "step"

  return (
    <RowBox {...rest}>
      <Hidden mdUp>
        <ActionCardInfo>
          <AdornmentIcon name="steps" tooltip={`${count} ${suffix}`} />
        </ActionCardInfo>
      </Hidden>
      <Hidden smDown>
        <AdornmentIcon name="steps" tooltip="Steps" />
        <Box ml={1} mr={mrInfo}>
          {count} {suffix}
        </Box>
      </Hidden>
    </RowBox>
  )
})

const ProcessRepeatActionCardInfo = memo(function ProcessRepeatActionCardInfo({ job, ...rest }) {
  const { getRepeatDisplayText, getRepeatDisplayShortText } = useJobUtils()

  if (job.process && job.process.repeat.type === PROCESS_REPEAT.NEVER) return null

  const repeatText = getRepeatDisplayText(job)

  return (
    <RowBox {...rest}>
      <Hidden mdUp>
        <ActionCardInfo>
          <AdornmentIcon name="repeat" tooltip={`Repeats: ${repeatText}`} />
        </ActionCardInfo>
      </Hidden>
      <Hidden smDown>
        <AdornmentIcon name="repeat" tooltip={`Repeats: ${repeatText}`} />
        <Box ml={1} mr={mrInfo} title={repeatText}>
          {getRepeatDisplayShortText(job)}
        </Box>
      </Hidden>
    </RowBox>
  )
})

const HumanTimeActionCardInfo = memo(function HumanTimeActionCardInfo({ adhoc, date, pinning, ...rest }) {
  const { getJobPinningTypeDescription } = useJobUtils()
  const pinned = Boolean(pinning?.type)
  const pinnedUntil = pinning?.until

  return (
    <RowBox {...rest}>
      <Hidden mdUp>
        <ActionCardInfo>
          {!adhoc && !pinned && (
            <AdornmentIcon
              name="time"
              tooltip={
                <>
                  Start: <FormatHumanTime value={date} />
                </>
              }
            />
          )}
          {pinned && pinnedUntil && <AdornmentIcon name="pinned" tooltip={<TimeAgo date={pinnedUntil} />} />}
          {pinned && !pinnedUntil && (
            <AdornmentIcon name="pinned" tooltip={getJobPinningTypeDescription({ pinning })} />
          )}
        </ActionCardInfo>
      </Hidden>
      <Hidden smDown>
        {!adhoc && !pinned && (
          <AdornmentIcon
            name="time"
            tooltip={
              <>
                Start: <FormatHumanTime value={date} />
              </>
            }
          />
        )}
        {pinned && <AdornmentIcon name="pinned" tooltip={getJobPinningTypeDescription({ pinning })} />}
        <Box ml={1} mr={mrInfo}>
          {!adhoc && !pinned && <FormatHumanTime value={date} />}
          {pinned && pinnedUntil && <TimeAgo date={pinnedUntil} />}
          {pinned && !pinnedUntil && getJobPinningTypeDescription({ pinning })}
        </Box>
      </Hidden>
    </RowBox>
  )
})

const DueTimeActionCardInfo = ({ job, forceComplete, ...rest }) => {
  const classes = useStyles()
  const { isJobOverdue } = useJobUtils()
  const { isBeforeToday } = useDateUtils()
  const {
    dueAt,
    status: { completed },
  } = job
  if (!dueAt) return null

  const isOverdue = isJobOverdue(job)
  const isOverdueAndBeforeToday = isOverdue && !forceComplete && isBeforeToday(job.dueAt)

  return (
    <RowBox {...rest}>
      <Hidden mdUp>
        <ActionCardInfo>
          <AdornmentIcon
            name="due"
            className={isOverdue && !forceComplete ? classes.overdueIcon : ""}
            tooltip={<>Due: {isOverdueAndBeforeToday ? <TimeAgo date={dueAt} /> : <FormatHumanTime value={dueAt} />}</>}
          />
        </ActionCardInfo>
      </Hidden>
      <Hidden smDown>
        <AdornmentIcon
          name="due"
          className={isOverdue && !forceComplete ? classes.overdueIcon : ""}
          tooltip={<>Due: {isOverdueAndBeforeToday ? <TimeAgo date={dueAt} /> : <FormatHumanTime value={dueAt} />}</>}
        />
        <Box ml={1} mr={mrInfo}>
          {isOverdueAndBeforeToday && <TimeAgo date={dueAt} overdue />}
          {!isOverdueAndBeforeToday && <FormatHumanTime value={dueAt} showOverdue={!completed} />}
        </Box>
      </Hidden>
    </RowBox>
  )
}

const AssigneesActionCardInfo = memo(function AssigneesActionCardInfo({
  job: { users, groups },
  compact,
  align = "right",
  ...rest
}) {
  const classes = useStyles()

  const right = align === "right"

  const assignees = !compact && (
    <Box className={right ? classes.assignees : ""}>
      <Assignees {...{ users, groups, limit: 1 }} />
    </Box>
  )
  const avatars = <AssigneeAvatars {...{ users, groups, limit: 2 }} />

  if (align === "left") {
    return (
      <RowBox {...rest}>
        {avatars}
        <Box ml={1}>{assignees}</Box>
      </RowBox>
    )
  }

  return (
    <RowBox {...rest}>
      {assignees}
      <Box ml={1}>{avatars}</Box>
    </RowBox>
  )
})

const ProcessActionCard = (props) => {
  const { job, onEdit, date, compact, navigable = true, onCantNavigate, cy } = props
  const {
    title,
    author,
    percentComplete,
    availableFrom,
    pinning,
    adhoc,
    status: { completed, steps },
  } = job
  const classes = useStyles()
  const { getNavigateToLink, isJobAfterToday, isJobOverdue } = useJobUtils()
  const { hasFeature } = useAuth()

  const isScheduled = isJobAfterToday(availableFrom)
  const isOverdue = !completed && isJobOverdue(job)

  const handleEdit = () => {
    if (onEdit) onEdit(job)
  }
  const handleDelete = () => {}

  const handleCantNavigateClick = () => {
    onCantNavigate && onCantNavigate()
  }

  if (steps.length === 1 && steps[0].responseType === TASK_RESPONSE_TYPE.NONE && hasFeature("process_single_step")) {
    return <SingleStepProcessActionCard {...props} />
  }

  return (
    <ActionCard
      title={title}
      completed={completed}
      overdue={isOverdue}
      cardProps={
        navigable
          ? { component: NavLink, to: { pathname: getNavigateToLink(job, date) }, "data-cy": cy }
          : { onClick: handleCantNavigateClick, "data-cy": cy }
      }
      isScheduled={isScheduled}
      rightChildren={
        <Box
          mr="auto"
          display="flex"
          flexDirection="row"
          alignSelf="center"
          alignItems="center"
          className={classes.rightChildren}
        >
          <Hidden xsDown>
            <Box className={classes.progress} mr={1}>
              <LinearProgress value={percentComplete} showValue overdue={isOverdue} />
            </Box>
          </Hidden>
          <Hidden xsUp>
            {!isScheduled && (
              <AuthorTools item={job} author={author.id} onEdit={handleEdit} subject="task" onDelete={handleDelete} />
            )}
          </Hidden>
        </Box>
      }
      footer={
        <>
          <RowBox>
            <ProcessStepsActionCardInfo count={steps.length} />
            <ProcessRepeatActionCardInfo job={job} />
            <HumanTimeActionCardInfo adhoc={adhoc} date={availableFrom} pinning={pinning} tooltip="Start" />
            <DueTimeActionCardInfo job={job} />
          </RowBox>
          <RowBox ml="auto">
            <AssigneesActionCardInfo job={job} compact={compact} />
          </RowBox>
        </>
      }
      cy={cy}
    >
      <Hidden smUp>
        <Box className={classes.progressXs} mr={1}>
          <LinearProgress value={percentComplete} showValue overdue={isOverdue} />
        </Box>
      </Hidden>
    </ActionCard>
  )
}

export {
  ProcessActionCard,
  HumanTimeActionCardInfo,
  DueTimeActionCardInfo,
  AssigneesActionCardInfo,
  ProcessStepsActionCardInfo,
  ProcessRepeatActionCardInfo,
}
